import { Dispatch } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { Auth, AuthProvider } from '@weddingspot/ws-api-client';
import * as types from '../types';
import { setCurUserProps, setCurUserPropsOnce, identifyCurUser, ClarityTags } from '@weddingspot/ws-analytics';

import { SET_USER_ANONYMOUS, SET_USER_AUTHED, SET_USER_INFO } from '../constants';
import { UserInfo } from '@weddingspot/ws-core-components';
import AppLogger from '../utils/logging';
const logger = AppLogger.createLogger();

export interface SetUserAuthed {
    type: SET_USER_AUTHED;
}

export interface SetUserAnonymous {
    type: SET_USER_ANONYMOUS;
}

export interface SetUserInfo {
    type: SET_USER_INFO;
    payload: UserInfo;
}

export type AuthAction = SetUserAnonymous | SetUserInfo | SetUserAuthed;

export function getUserInfo() {
    return AuthProvider.userInfo()
        .then((info: Auth.UserInfo) => {
            return {
                id: info.id,
                external_id: info.external_id,
                firstName: info.first_name,
                lastName: info.last_name,
                email: info.email,
                phoneNumber: info.phone_number,
                isStaff: info.is_staff,
                isSuperuser: info.is_superuser,
                unreadMessagesCount: info.unread_messages_count,
                lastMarket: info.last_market,
                dateJoined: new Date(info.date_joined),
                isWeddingVendor: info.is_wedding_vendor,
                isPaidWeddingVendor: info.wedding_vendor_paid,
                numFavorites: info.num_favorites,
                lastEstimateDate: info.last_estimate_date ? new Date(info.last_estimate_date) : undefined,
                lastAppointmentDate: info.last_appointment_date ? new Date(info.last_appointment_date) : undefined,
            };
        })
        .catch((err) => {
            logger.error(err);
            return err;
        });
}

export function setUserInfo(userInfo: UserInfo): (dispatch: Dispatch<SetUserInfo>) => void {
    return function (dispatch: Dispatch<SetUserInfo>) {
        dispatch({
            type: SET_USER_INFO,
            payload: userInfo,
        });

        identifyCurUser(userInfo.id);
        setCurUserProps({
            $first_name: userInfo.firstName,
            $last_name: userInfo.lastName,
            $email: userInfo.email,
            $created: userInfo.dateJoined,
            'Last Market': userInfo.lastMarket || 'Not Set',
            'Is Staff': userInfo.isStaff,
            'Is Wedding Vendor': userInfo.isWeddingVendor,
            'Wedding Vendor Paid': userInfo.isPaidWeddingVendor,
            'Num Favorites': userInfo.numFavorites,
            'Last Spot Estimate Date': userInfo.lastEstimateDate,
            'Last Appointment Request Date': userInfo.lastAppointmentDate,
        });
        setCurUserPropsOnce({
            'Wedding Spot ID': `${userInfo.id}`,
            'Is Registered': true,
        });
        const userType = userInfo.isStaff ? 'Staff' : userInfo.isWeddingVendor ? 'Vendor' : 'Couple';
        setCurUserProps({ name: ClarityTags.USER_TYPE, value: userType });
    };
}

/**
 * Try to get and set user info. If it fails, dispatch user anonymous
 */
export function tryGetUserInfo() {
    return function (dispatch: ThunkDispatch<types.StoreState, undefined, SetUserInfo>) {
        return getUserInfo()
            .then((info: UserInfo) => {
                dispatch(setUserInfo(info));
                dispatch(setUserAuthed());
            })
            .catch(() => {
                dispatch(setUserAnonymous());
            });
    };
}

export function setUserAuthed(): (dispatch: Dispatch<SetUserAuthed>) => void {
    return function (dispatch: Dispatch<SetUserAuthed>) {
        dispatch({
            type: SET_USER_AUTHED,
        });
        setCurUserProps({ name: ClarityTags.USER_REGISTERED, value: 'Yes' });
    };
}

export function setUserAnonymous(): (dispatch: Dispatch<SetUserAnonymous>) => void {
    return function (dispatch: Dispatch<SetUserAnonymous>) {
        dispatch({
            type: SET_USER_ANONYMOUS,
        });
        setCurUserProps({ name: ClarityTags.USER_REGISTERED, value: 'No' });
    };
}
